import React, { useContext } from 'react'
import { OffreModel } from '../types/OffreModel'
import { Sticky, StickyContainer } from 'react-sticky'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { Grid } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import thousandsSeparator from '../../../utils/formatter/thousandsSeparator'
import { dateAvecPoints } from '../../../utils/formatter/formatDate'
import styles from '../../../styles'
import SvgSurface from '../../../icons/SvgSurface'
import { useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { lotTypesEnum } from '../../../enums/lot/lotTypesEnum'
import BoutonSelectionner from './BoutonSelectionner'
import { fontFamilyMonserrat } from '../../../theme'

const useStyles = makeStyles()(
	(theme) => ({
		stickyContainer: {
			width: '100%'
		},
		caracteristiquesContainer: {
			padding: '16px 0 30px',
			borderRadius: 20,
			border: '1px solid #EFF3F5',
			boxShadow: '0px 0px 10px #12294119'
		},
		prixContainer: {
			paddingBottom: 17,
			borderBottom: '1px solid #EFF3F5'
		},
		prixLabel: {
			...styles(theme).text29,
			paddingLeft: 36
		},
		prixValeur: {
			...styles(theme).text3,
			lineHeight: '1',

			'& span': {
				...styles(theme).text19,
				fontSize: 23
			}
		},
		atoutsContainer: {
			padding: '26px 0px 7px',
			borderBottom: '1px solid #EFF3F5'
		},
		atoutContainer: {
			marginBottom: 24,

			'&:nth-child(odd)': {
				paddingLeft: 35
			},
			'& img': {
				objectFit: 'contain'
			}
		},
		atoutTexte: {
			...styles(theme).text2,
			margin: 0,
			marginLeft: 14,
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			width: '70%'
		},
		dateContainer: {
			padding: '20px 0 0'
		},
		dateLabel: {
			...styles(theme).text19,
			fontSize: 23,
			margin: 0
		},
		dateBoutonContainer: {
			marginTop: 22,
			marginBottom: 22
		},
		contactezNousStyle: {
			textDecoration: 'underline'
		},
		referenceStyle: {
			padding: '0px 36px',
			fontSize: 12,
			fontFamily: fontFamilyMonserrat,
			fontWeight: 500,
			paddingBottom: 20
		}
	})
)

interface AcceptingProps {
	offre: OffreModel
}

type CaracteristiquesProps = AcceptingProps

const Caracteristiques: React.FC<CaracteristiquesProps> = (
	{
		offre
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes } = useStyles()
	const intl = useIntl()
	const theme = useTheme()
	const offset = isLargeScreen ? 100 : 65

	const CaracteristiquesComponent = () => (
		<Grid container item xs={12} className={classes.caracteristiquesContainer}>
			{/*	Ligne contenant l'information sur prix*/}
			<Grid container item xs={12} alignItems="center" className={classes.prixContainer}>
				<Grid item xs={6} className={classes.prixLabel}>
					<FormattedMessage
						id="offre.caracteristiques.prix.label"
						defaultMessage="Loyer / mois"
						description="Information sur le prix du loyer"
					/>
				</Grid>

				<Grid item xs={6} className={classes.prixValeur}>
					<FormattedMessage
						id="offre.caracteristiques.prix.valeur"
						defaultMessage="{prix} {br} Charges comprises"
						description="Valeur du prix du loyer"
						values={{
							prix: <span>{thousandsSeparator(offre.tarification.prixMensuel, false)} €</span>,
							br: <br />
						}}
					/>
				</Grid>
			</Grid>

			{/* Bloc contenant les atouts du bien */}
			{
				offre.type === lotTypesEnum.apartment && (
					<Grid container item xs={12} className={classes.atoutsContainer}>

						<Grid container item xs={6} className={classes.atoutContainer}>
							<SvgSurface width={21} height={21} color={theme.palette.primary.main} />

							<span className={classes.atoutTexte}>
								{offre.surface}

								<FormattedMessage
									id="global.surface"
									defaultMessage=" m²"
									description="Atout surface"
								/>
							</span>
						</Grid>

						{
							offre.atouts.slice(0, 3).map(
								(atout, index) => (
									<Grid
										container
										item
										xs={6}
										alignItems="center"
										key={`caracteristique-atout-${atout.texte}-${index}`}
										className={classes.atoutContainer}
									>
										<img src={atout.icone} alt={atout.texte} width={21} height={21} />

										<span className={classes.atoutTexte}>{atout.texte}</span>
									</Grid>
								)
							)
						}
					</Grid>
				)
			}

			{/* Bloc contenant les informations sur la date de disponibilité et les éléments de contact */}
			<Grid container item xs={12} justifyContent="center" className={classes.dateContainer}>
				<Grid container item>
					<span className={classes.referenceStyle}>
						<FormattedMessage
							id="offre.caracteristiques.reference"
							defaultMessage="Référence : {ref}"
							description="Référence de l'offre"
							values={{
								ref: offre.idMgcCrm
							}}
						/>
					</span>
				</Grid>
				<Grid container item xs={12} justifyContent="center">
					<p className={classes.dateLabel}>
						{
							offre.type === lotTypesEnum.apartment ? (
								new Date(offre.dateDisponibilite) < new Date() ? (
									<FormattedMessage
										id="offre.caracteristiques.available"
										defaultMessage="Disponible"
										description="Information sur la date de disponibilité"
									/>
								) : (
									<FormattedMessage
										id="offre.caracteristiques.dateDisponibilite"
										defaultMessage="Disponible le {date}"
										description="Information sur la date de disponibilité"
										values={{
											date: dateAvecPoints(new Date(offre.dateDisponibilite), intl.locale)
										}}
									/>
								)
							) : (
								<FormattedMessage
									id="offre.caracteristiques.disponibleMaintenant"
									defaultMessage="Disponible"
									description="Information sur la date de disponibilité"
								/>
							)
						}
					</p>
				</Grid>

				<Grid container item xs={11} className={classes.dateBoutonContainer}>
					<BoutonSelectionner offre={offre} />
				</Grid>
			</Grid>
		</Grid>
	)

	return (
		<StickyContainer className={classes.stickyContainer}>
			{
				isLargeScreen ? (
					<Sticky
						topOffset={-offset}
					>
						{
							(
								{
									style,
									distanceFromBottom,
									isSticky
								}
							) => (
								<div
									style={{
										...style,
										top: isSticky ? offset : 'auto',
										zIndex: 1
									}}
								>
									<CaracteristiquesComponent />
								</div>
							)
						}
					</Sticky>
				) : (
					<CaracteristiquesComponent />
				)
			}
		</StickyContainer>
	)
}

export default Caracteristiques